import { cn } from '../../lib/utils';

export type SwitchSize = 'sm' | 'md';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Accessible name when there is no visible <label> wired to `id`. */
  label?: string;
  id?: string;
  size?: SwitchSize;
  className?: string;
}

const TRACK: Record<SwitchSize, string> = {
  sm: 'h-4.5 w-8',
  md: 'h-5 w-9',
};

const THUMB: Record<SwitchSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
};

const TRANSLATE: Record<SwitchSize, { on: string; off: string }> = {
  sm: { on: 'translate-x-4', off: 'translate-x-0.5' },
  md: { on: 'translate-x-4.5', off: 'translate-x-0.5' },
};

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
  id,
  size = 'md',
  className,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full transition-colors duration-200',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        TRACK[size],
        checked ? 'bg-accent' : 'bg-border-strong',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span
        className={cn(
          'inline-block rounded-full bg-white shadow-hair transition-transform duration-200 ease-out',
          THUMB[size],
          checked ? TRANSLATE[size].on : TRANSLATE[size].off,
        )}
      />
    </button>
  );
}

export interface SwitchFieldProps extends SwitchProps {
  title: string;
  description?: string;
}

/** Switch with a title/description block — the settings-row pattern. */
export function SwitchField({ title, description, className, ...props }: SwitchFieldProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4 py-3', className)}>
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-medium text-fg">{title}</p>
        {description && <p className="text-xs text-muted">{description}</p>}
      </div>
      <Switch {...props} label={props.label ?? title} />
    </div>
  );
}
